/**
 * Shared presentation shell for custom chart tooltips.
 * Renders the common card layout (title, optional header accessory, and rows)
 * while callers supply already formatted content.
 */

import type { ReactNode } from 'react';
import { tbColors } from 'styles/colors';

export interface ChartTooltipCardValue {
  key?: string;
  content: ReactNode;
  color?: string;
  minWidth?: number;
  title?: string;
  weight?: number;
}

export interface ChartTooltipCardRow {
  key?: string;
  label: ReactNode;
  value?: ReactNode;
  values?: ChartTooltipCardValue[];
  leadingValue?: ReactNode;
  title?: string;
  labelColor?: string;
  valueColor?: string;
  leadingValueColor?: string;
  withTopDivider?: boolean;
}

interface ChartTooltipCardProps {
  title: ReactNode;
  rows: ChartTooltipCardRow[];
  headerAccessory?: ReactNode;
  minWidth?: number;
  maxWidth?: number;
}

export function ChartTooltipCard({
  title,
  rows,
  headerAccessory = undefined,
  minWidth = undefined,
  maxWidth = 520,
}: ChartTooltipCardProps) {
  const hasLeadingValues = rows.some(row => row.leadingValue !== undefined);
  const normalizedRows = rows.map(row => ({
    ...row,
    values: (
      row.values ?? [
        {
          content: row.value,
          color: row.valueColor,
        },
      ]
    ).slice(0, 2),
  }));
  const valueColumnCount = Math.max(1, ...normalizedRows.map(row => row.values.length));
  const valueColumns = Array.from({ length: valueColumnCount }, (_, index) => {
    const minWidth = Math.max(...normalizedRows.map(row => row.values[index]?.minWidth ?? 108));

    return `minmax(${minWidth}px, max-content)`;
  }).join(' ');

  return (
    <div
      style={{
        ...(minWidth !== undefined ? { minWidth } : {}),
        maxWidth,
        backgroundColor: tbColors.all.snow[50],
        color: tbColors.all.ash[900],
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        padding: 12,
        width: 'fit-content',
      }}
    >
      <div
        style={{
          alignItems: 'center',
          columnGap: 8,
          display: 'grid',
          gridTemplateColumns: headerAccessory ? 'minmax(0, 1fr) auto' : 'minmax(0, 1fr)',
        }}
      >
        <div
          style={{
            fontSize: 16,
            fontWeight: 600,
            lineHeight: '24px',
            letterSpacing: 0,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </div>
        {headerAccessory}
      </div>
      <div
        style={{
          borderTop: `1px solid ${tbColors.all.snow[200]}`,
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          paddingTop: 12,
        }}
      >
        {normalizedRows.map(row => {
          return (
            <div
              key={row.key ?? String(row.label)}
              style={{
                display: 'grid',
                gridTemplateColumns: hasLeadingValues
                  ? `auto minmax(0, 1fr) ${valueColumns}`
                  : `minmax(0, 1fr) ${valueColumns}`,
                alignItems: 'baseline',
                columnGap: 8,
                fontSize: 14,
                lineHeight: '21px',
                letterSpacing: 0,
                ...(row.withTopDivider
                  ? {
                      borderTop: `1px solid ${tbColors.all.snow[200]}`,
                      marginTop: 6,
                      paddingTop: 12,
                    }
                  : {}),
              }}
            >
              {hasLeadingValues && (
                <strong
                  style={{
                    color: row.leadingValueColor ?? tbColors.all.ash[900],
                    fontSize: 14,
                    lineHeight: '21px',
                    minWidth: 72,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {row.leadingValue ?? null}
                </strong>
              )}
              <span
                style={{
                  color: row.labelColor ?? tbColors.all.ash[900],
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={row.title}
              >
                {row.label}
              </span>
              {Array.from({ length: valueColumnCount }).map((_, index) => {
                const value = row.values[index];

                return (
                  <strong
                    key={value?.key ?? index}
                    title={value?.title}
                    style={{
                      color: value?.color ?? tbColors.all.ash[900],
                      fontSize: 14,
                      fontWeight: value?.weight ?? (index === 0 ? 600 : 300),
                      lineHeight: '21px',
                      minWidth: value?.minWidth ?? 108,
                      overflow: 'hidden',
                      paddingLeft: value?.content === undefined ? 0 : 8,
                      paddingRight: value?.content === undefined ? 0 : 8,
                      textAlign: 'right',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {value?.content ?? null}
                  </strong>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
